"use client";

import React, { useMemo } from "react";
import { FeaturedContentBlock, type ImageComponent } from "fansunited-sports-ui";
import { useCompetitionHomeData } from "../../../context/CompetitionHomeDataContext";
import { ArticleGrid } from "../../../partials/articles/ArticleGrid";
import { MatchCarousel } from "../../../partials/matches/MatchCarousel";
import { StandingsHorizontal } from "../../../partials/standings/StandingsHorizontal";
import { LeadersTables } from "../../../partials/players/LeadersTables";
import { SeasonStatsCards } from "../../../partials/stats/SeasonStatsCards";
import { PromoSlot } from "@/features/promo-slots/PromoSlot";
import { AdSlot } from "@/features/ads/AdSlot";
import { SectionStrip, type BrandedStyle } from "@/components/layout/SectionStrip";
import {
    HOME_HERO,
    HOME_MATCHES,
    HOME_STANDINGS,
    HOME_ARTICLES,
    HOME_LEADERS,
    HOME_STATS,
} from "@/features/theme/sectionKeys";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";
import { getPostImageUrl } from "@/components/ui/PostImage";
import { useNavigation } from "@/lib/navigation/NavigationProvider";

/**
 * ContentMagazineLayout — Content-Rich magazine-style homepage.
 *
 * For sites with heavy editorial + video production.
 *
 * 1. Match Carousel (full width)
 * 2. Featured Content Block (6 articles — spotlight layout)
 * 3. Promo slot 0 (full width)
 * 4. Video Highlights
 * 5. Standings Horizontal (accent strip)
 * 6. Article Grid (3 more)
 * 7. Video Shorts
 * 8. Promo slot 1
 * 9. Leaders
 * 10. Season Stats
 */
interface Props {
    videoShortsSlot?: React.ReactNode;
    videoHighlightsSlot?: React.ReactNode;
    sectionStyles?: Record<string, BrandedStyle>;
}

export function FootballCompetitionHomeContentMagazineLayout({
    videoShortsSlot,
    videoHighlightsSlot,
    sectionStyles = {},
}: Props = {}) {
    const {
        latestNews,
        featuredPosts,
        promos,
        leaders,
        standings,
        homeSlices,
        competitionStats,
        goalDistribution,
        hasCompetitionStats,
    } = useCompetitionHomeData();
    const nav = useNavigation();

    const standingsEntries = useMemo(() => standings?.entries ?? [], [standings]);

    // Map articles to FeaturedContentBlock items — use featured posts if available
    const spotlightSource = featuredPosts?.length ? featuredPosts : latestNews;
    const spotlightPosts = useMemo(() => spotlightSource.slice(0, 6), [spotlightSource]);
    const featuredItems = useMemo(
        () =>
            spotlightPosts.map((post) => ({
                content: {
                    id: String(post.id),
                    type: "article" as const,
                    link: nav.post(post),
                    title: post.title,
                    description: post.summary || undefined,
                    image: {
                        url: getPostImageUrl(post, "medium"),
                        alt: post.title,
                    },
                    gradient: "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 30%, transparent 50%)",
                },
            })),
        [spotlightPosts, nav]
    );

    const gridArticles = latestNews.slice(6, 9);

    return (
        <div className="min-h-screen bg-background">
            <main className="max-w-350 mx-auto px-4 py-6 space-y-10">
                {/* Ad: content-top */}
                <AdSlot id="content-top" />

                {/* 1. Featured Content Block — branded strip */}
                {featuredItems.length > 0 && (
                    <SectionStrip brandedStyle={sectionStyles?.[HOME_HERO]}>
                        <MediaVariantsProvider
                            media={spotlightPosts.map((p) => p.heroImage)}
                            sizes="(max-width: 1024px) 100vw, 66vw"
                        >
                            <FeaturedContentBlock
                                variant="spotlight"
                                items={featuredItems}
                                ImageComponent={FillImage as ImageComponent}
                                priority
                                styles={{
                                    gap: "sm",
                                    cardStyles: {
                                        shadow: "none",
                                        typography: {
                                            title: "text-xl font-bold mb-1!",
                                            description: "text-xs md:text-sm text-white/70 line-clamp-2",
                                        },
                                    },
                                }}
                            />
                        </MediaVariantsProvider>
                    </SectionStrip>
                )}

                {/* 2. Match Carousel */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_MATCHES]}>
                    <MatchCarousel />
                </SectionStrip>

                {/* Ad: mid-scroll-1 */}
                <AdSlot id="mid-scroll-1" />

                {/* 3. Promo slot 0 */}
                <PromoSlot row={promos[0] ?? null} />

                {/* 4. Video Highlights */}
                {videoHighlightsSlot}

                {/* 5. Standings Horizontal */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_STANDINGS]}>
                    <StandingsHorizontal standings={standingsEntries} />
                </SectionStrip>

                {/* 6. Article Grid (3 more) */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_ARTICLES]}>
                    {gridArticles.length > 0 && <ArticleGrid articles={gridArticles} />}
                </SectionStrip>

                {/* Ad: mid-scroll-2 */}
                <AdSlot id="mid-scroll-2" />

                {/* 7. Video Shorts */}
                {videoShortsSlot}

                {/* 8. Promo slot 1 */}
                <PromoSlot row={promos[1] ?? null} />

                {/* 9. Leaders */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_LEADERS]}>
                    <LeadersTables leaders={leaders} />
                </SectionStrip>

                {/* Ad: mid-scroll-3 */}
                <AdSlot id="mid-scroll-3" />

                {/* 10. Season Stats */}
                <SectionStrip brandedStyle={sectionStyles?.[HOME_STATS]}>
                    {hasCompetitionStats && (
                        <SeasonStatsCards
                            competitionStats={competitionStats}
                            goalDistribution={goalDistribution}
                            teamWithMostWins={homeSlices.teamWithMostWins}
                        />
                    )}
                </SectionStrip>

                {/* Promo slot 2 */}
                <PromoSlot row={promos[2] ?? null} />

                {/* Ad: content-bottom */}
                <AdSlot id="content-bottom" />
            </main>
        </div>
    );
}
